import { useCallback, useMemo, useState } from 'react'

import { Button, Group, Modal, Select, TextInput, Title } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconDeviceFloppy } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useGetList, useNotify, useTranslate } from 'react-admin'
import { ScamExportFiltersApiDTO } from '../../../codegen/generated/Api.schemas'
import { useExportsContext, useExportsDispatchContext } from '../../../contexts/ExportsContext'
import { API } from '../../../helpers/Api.helper'
import { ScamExportPreset } from '../../../types/ScamExportPresets'

export const SCAMExportsPresets = () => {
  const notify = useNotify()
  const t = useTranslate()
  const context = useExportsContext()
  const { setSCAMFilters } = useExportsDispatchContext()

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)
  const [selectedPresetId, setSelectedPresetId] = useState<ScamExportPreset['id'] | null>()
  const [newPresetName, setPresetName] = useState<string>('')

  const savePresetMutation = useMutation({
    mutationKey: ['scamExportPresets', context.scam.filters],
    mutationFn: (body: { name: string; filters: ScamExportFiltersApiDTO }) => {
      return API.post('/api/scamExportPresets', body)
    },
  })

  const updatePresetMutation = useMutation({
    mutationKey: ['scamExportPresets', 'update'],
    mutationFn: ({
      id,
      ...body
    }: { id: string; name: string; filters: ScamExportFiltersApiDTO }) => {
      return API.put(`/api/scamExportPresets/${id}`, body)
    },
  })

  const presetsQuery = useGetList<ScamExportPreset>('scamExportPresets', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const presets = useMemo(
    () =>
      (presetsQuery.data ?? []).map(p => ({
        value: p.id,
        label: p.name,
      })),
    [presetsQuery.data],
  )

  const onPresetChange = useCallback(
    (pId: string | null) => {
      setSelectedPresetId(pId)

      const selectedPreset = presetsQuery.data?.find(p => p.id === pId)

      if (selectedPreset) {
        setSCAMFilters(selectedPreset.filters)
      } else {
        setSCAMFilters({})
      }
    },
    [presetsQuery.data, setSCAMFilters],
  )

  const onSavePreset = useCallback(async () => {
    try {
      const { filters } = context.scam
      if (filters) {
        const res = await savePresetMutation.mutateAsync({
          name: newPresetName,
          filters,
        })
        presetsQuery.refetch()

        notify(t('pages.exports.toast_createPresetSuccess'), {
          type: 'success',
        })

        closeModal()
        setSelectedPresetId(res.data.id)
        setPresetName('')
      }
    } catch (e: any) {
      console.error(`Error - create preset - ${e}`)
      notify(
        `${t('pages.exports.toast_createPresetError')} - ${
          e.response?.data?.message ? e.response.data.message : e
        }`,
        { type: 'error' },
      )
    }
  }, [
    context.scam,
    newPresetName,
    savePresetMutation.mutateAsync,
    presetsQuery.refetch,
    closeModal,
    notify,
  ])

  const onUpdatePreset = useCallback(async () => {
    const selectedPreset = presetsQuery.data?.find(p => p.id === selectedPresetId)
    const { filters } = context.scam
    if (!selectedPreset || !filters) {
      return
    }

    try {
      await updatePresetMutation.mutateAsync({
        id: selectedPreset.id,
        name: selectedPreset.name,
        filters,
      })
      presetsQuery.refetch()

      notify(t('pages.exports.toast_updatePresetSuccess'), {
        type: 'success',
      })
    } catch (e: any) {
      console.error(`Error - update preset - ${e}`)
      notify(
        `${t('pages.exports.toast_updatePresetError')} - ${
          e.response?.data?.message ? e.response.data.message : e
        }`,
        { type: 'error' },
      )
    }
  }, [
    context.scam,
    selectedPresetId,
    presetsQuery.data,
    updatePresetMutation.mutateAsync,
    presetsQuery.refetch,
    notify,
  ])

  return (
    <>
      <Group>
        <Select
          label="Presets"
          data={presets}
          value={selectedPresetId}
          onChange={onPresetChange}
          searchable
          clearable
          autoComplete="off"
          data-form-type="other"
        />
        {selectedPresetId ? (
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => onUpdatePreset()}
            leftSection={<IconDeviceFloppy size={14} />}
            maw={250}
            mt="md"
            loading={updatePresetMutation.isLoading}
            disabled={!presetsQuery.data?.some(p => p.id === selectedPresetId)}
          >
            {t('pages.exports.updatePreset')}
          </Button>
        ) : (
          <Button
            variant="subtle"
            size="compact-sm"
            onClick={() => openModal()}
            leftSection={<IconDeviceFloppy size={14} />}
            maw={250}
            mt="md"
          >
            {t('pages.exports.savePreset')}
          </Button>
        )}
      </Group>
      <Modal
        title={<Title size="h3">{t('pages.exports.savePreset')}</Title>}
        opened={isModalOpened}
        onClose={closeModal}
      >
        <TextInput
          mt="md"
          label={t('pages.exports.presetName')}
          placeholder={t('placeholders.presetName')}
          value={newPresetName}
          onChange={e => setPresetName(e.currentTarget.value)}
        />
        <Group justify="right" mt="xl">
          <Button variant="subtle" onClick={() => closeModal()}>
            {t('_generics.cancel')}
          </Button>
          <Button
            variant="filled"
            onClick={() => onSavePreset()}
            loading={savePresetMutation.isLoading}
            disabled={newPresetName.length <= 2}
          >
            {t('_generics.save')}
          </Button>
        </Group>
      </Modal>
    </>
  )
}
